import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Building2, MapPin, Save, Upload, Edit2, X, Printer } from 'lucide-react';
import { hospitalsApi, locationsApi } from '@/api/client';
import { toast } from '@/utils/toast';

const hospitalSchema = z.object({
    name: z.string().min(1, 'Hospital name is required'),
    disclaimerText: z.string().optional(),
    estimateValidityDays: z.number().int().positive().default(30),
});

const locationSchema = z.object({
    name: z.string().min(1, 'Location name is required'),
    address: z.string().optional(),
    city: z.string().optional(),
    phone: z.string().optional(),
    printHeaderSpacing: z.number().int().min(0).max(500).optional(),
    printFooterSpacing: z.number().int().min(0).max(500).optional(),
});

type HospitalFormData = z.infer<typeof hospitalSchema>;
type LocationFormData = z.infer<typeof locationSchema>;

export function SettingsPage() {
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [editingLocation, setEditingLocation] = useState<any>(null);
    const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
    const queryClient = useQueryClient();

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const hospitalId = user.hospitalId;

    const { data: hospital, isLoading: hospitalLoading } = useQuery({
        queryKey: ['hospital', hospitalId],
        queryFn: () => hospitalsApi.getById(hospitalId),
        enabled: !!hospitalId,
    });

    const { data: locations = [], isLoading: locationsLoading } = useQuery({
        queryKey: ['locations'],
        queryFn: locationsApi.list,
    });

    const updateHospitalMutation = useMutation({
        mutationFn: (data: Partial<HospitalFormData>) =>
            hospitalsApi.update(hospitalId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['hospital'] });
            toast.success('Hospital settings saved successfully');
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.error || 'Failed to save hospital settings');
        },
    });

    const uploadLogoMutation = useMutation({
        mutationFn: (file: File) => hospitalsApi.uploadLogo(hospitalId, file),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['hospital'] });
            toast.success('Logo uploaded successfully');
            setLogoFile(null);
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.error || 'Failed to upload logo');
        },
    });

    const createLocationMutation = useMutation({
        mutationFn: locationsApi.create,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['locations'] });
            toast.success('Location created successfully');
            setIsLocationModalOpen(false);
            setEditingLocation(null);
            resetLocation({
                name: '',
                address: '',
                city: '',
                phone: '',
                printHeaderSpacing: 100,
                printFooterSpacing: 100,
            });
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.error || 'Failed to create location');
        },
    });

    const updateLocationMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: Partial<LocationFormData> }) =>
            locationsApi.update(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['locations'] });
            toast.success('Location updated successfully');
            setIsLocationModalOpen(false);
            setEditingLocation(null);
            resetLocation({
                name: '',
                address: '',
                city: '',
                phone: '',
                printHeaderSpacing: 100,
                printFooterSpacing: 100,
            });
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.error || 'Failed to update location');
        },
    });

    const {
        register: registerHospital,
        handleSubmit: handleSubmitHospital,
        formState: { errors: hospitalErrors },
    } = useForm<HospitalFormData>({
        resolver: zodResolver(hospitalSchema),
        values: hospital || {},
    });

    const {
        register: registerLocation,
        handleSubmit: handleSubmitLocation,
        formState: { errors: locationErrors },
        reset: resetLocation,
    } = useForm<LocationFormData>({
        resolver: zodResolver(locationSchema),
        defaultValues: editingLocation || {},
    });

    const onSubmitHospital = (data: HospitalFormData) => {
        updateHospitalMutation.mutate(data);
    };

    const onSubmitLocation = (data: LocationFormData) => {
        if (editingLocation) {
            updateLocationMutation.mutate({ id: editingLocation.id, data });
        } else {
            createLocationMutation.mutate(data);
        }
    };

    const handleEditLocation = (location: any) => {
        setEditingLocation(location);
        resetLocation(location);
        setIsLocationModalOpen(true);
    };

    const handleCloseLocationModal = () => {
        setIsLocationModalOpen(false);
        setEditingLocation(null);
        resetLocation({
            name: '',
            address: '',
            city: '',
            phone: '',
            printHeaderSpacing: 100,
            printFooterSpacing: 100,
        });
    };

    const handleLogoUpload = () => {
        if (logoFile) {
            uploadLogoMutation.mutate(logoFile);
        }
    };

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
                <p className="text-slate-600">Manage hospital and location settings</p>
            </div>

            {/* Hospital Settings */}
            <div className="card">
                <div className="card-header">
                    <div className="flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-primary-600" />
                        <h2 className="text-lg font-semibold">Hospital Information</h2>
                    </div>
                </div>

                {hospitalLoading ? (
                    <div className="card-body">
                        <div className="animate-pulse space-y-4">
                            <div className="h-10 bg-slate-100 rounded" />
                            <div className="h-10 bg-slate-100 rounded" />
                            <div className="h-24 bg-slate-100 rounded" />
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleSubmitHospital(onSubmitHospital)}>
                        <div className="card-body space-y-4">
                            {/* Logo Upload */}
                            <div>
                                <label className="label">Hospital Logo</label>
                                <div className="flex items-center gap-4">
                                    {hospital?.logoUrl && (
                                        <img
                                            src={hospital.logoUrl}
                                            alt="Hospital logo"
                                            className="w-16 h-16 object-contain border border-slate-200 rounded-lg"
                                        />
                                    )}
                                    <div className="flex-1">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                                            className="text-sm"
                                        />
                                        {logoFile && (
                                            <button
                                                type="button"
                                                onClick={handleLogoUpload}
                                                disabled={uploadLogoMutation.isPending}
                                                className="btn btn-primary btn-sm mt-2"
                                            >
                                                <Upload className="w-4 h-4" />
                                                {uploadLogoMutation.isPending ? 'Uploading...' : 'Upload Logo'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Hospital Name */}
                            <div>
                                <label className="label">Hospital Name *</label>
                                <input
                                    type="text"
                                    className={`input ${hospitalErrors.name ? 'input-error' : ''}`}
                                    {...registerHospital('name')}
                                />
                                {hospitalErrors.name && (
                                    <p className="error-text">{hospitalErrors.name.message}</p>
                                )}
                            </div>

                            {/* Estimate Validity */}
                            <div>
                                <label className="label">Estimate Validity (days)</label>
                                <input
                                    type="number"
                                    min="1"
                                    className="input max-w-xs"
                                    {...registerHospital('estimateValidityDays', { valueAsNumber: true })}
                                />
                                <p className="text-xs text-slate-500 mt-1">
                                    Number of days the estimate remains valid
                                </p>
                            </div>

                            {/* Disclaimer */}
                            <div>
                                <label className="label">Estimate Disclaimer</label>
                                <textarea
                                    rows={4}
                                    className="input"
                                    placeholder="This is an estimate only. Actual charges may vary..."
                                    {...registerHospital('disclaimerText')}
                                />
                                <p className="text-xs text-slate-500 mt-1">
                                    This text will appear on all estimate PDFs
                                </p>
                            </div>
                        </div>

                        <div className="card-footer flex justify-end">
                            <button
                                type="submit"
                                disabled={updateHospitalMutation.isPending}
                                className="btn btn-primary btn-md"
                            >
                                <Save className="w-4 h-4" />
                                {updateHospitalMutation.isPending ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </form>
                )}
            </div>

            {/* Locations */}
            <div className="card">
                <div className="card-header flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <MapPin className="w-5 h-5 text-primary-600" />
                        <h2 className="text-lg font-semibold">Locations</h2>
                    </div>
                    <button
                        onClick={() => setIsLocationModalOpen(true)}
                        className="btn btn-primary btn-sm"
                    >
                        Add Location
                    </button>
                </div>

                <div className="card-body">
                    {locationsLoading ? (
                        <div className="space-y-3">
                            {[...Array(2)].map((_, i) => (
                                <div key={i} className="h-20 bg-slate-100 rounded-lg animate-pulse" />
                            ))}
                        </div>
                    ) : locations.length === 0 ? (
                        <div className="text-center py-8">
                            <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                            <p className="text-slate-500">No locations yet</p>
                            <button
                                onClick={() => setIsLocationModalOpen(true)}
                                className="btn btn-primary btn-sm mt-2"
                            >
                                Add First Location
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {locations.map((location: any) => (
                                <div
                                    key={location.id}
                                    className="p-4 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors"
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <h3 className="font-medium text-slate-900">{location.name}</h3>
                                            {location.address && (
                                                <p className="text-sm text-slate-600 mt-1">{location.address}</p>
                                            )}
                                            <div className="flex gap-4 mt-2 text-xs text-slate-500">
                                                {location.city && <span>📍 {location.city}</span>}
                                                {location.phone && <span>📞 {location.phone}</span>}
                                            </div>
                                            {(location.printHeaderSpacing !== undefined || location.printFooterSpacing !== undefined) && (
                                                <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                                                    <Printer className="w-3 h-3" />
                                                    <span>
                                                        Print spacing: {location.printHeaderSpacing ?? 100}px / {location.printFooterSpacing ?? 100}px
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => handleEditLocation(location)}
                                                className="btn btn-ghost btn-sm"
                                                title="Edit location"
                                            >
                                                <Edit2 className="w-4 h-4" />
                                            </button>
                                            <span
                                                className={`badge ${location.isActive ? 'badge-success' : 'badge-error'
                                                    }`}
                                            >
                                                {location.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Location Modal */}
            {isLocationModalOpen && (
                <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
                    <div className="card w-full max-w-lg">
                        <div className="card-header flex items-center justify-between">
                            <h2 className="text-lg font-semibold">
                                {editingLocation ? 'Edit Location' : 'Add New Location'}
                            </h2>
                            <button onClick={handleCloseLocationModal} className="p-1 hover:bg-slate-100 rounded">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmitLocation(onSubmitLocation)}>
                            <div className="card-body space-y-4">
                                <div>
                                    <label className="label">Location Name *</label>
                                    <input
                                        type="text"
                                        className={`input ${locationErrors.name ? 'input-error' : ''}`}
                                        placeholder="Main Campus"
                                        {...registerLocation('name')}
                                    />
                                    {locationErrors.name && (
                                        <p className="error-text">{locationErrors.name.message}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="label">Address</label>
                                    <input
                                        type="text"
                                        className="input"
                                        placeholder="123 Medical Street"
                                        {...registerLocation('address')}
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="label">City</label>
                                        <input
                                            type="text"
                                            className="input"
                                            placeholder="Mumbai"
                                            {...registerLocation('city')}
                                        />
                                    </div>
                                    <div>
                                        <label className="label">Phone</label>
                                        <input
                                            type="tel"
                                            className="input"
                                            placeholder="+91 22 1234 5678"
                                            {...registerLocation('phone')}
                                        />
                                    </div>
                                </div>

                                {/* Print Settings */}
                                <div className="border-t pt-4 mt-2">
                                    <div className="flex items-center gap-2 mb-3">
                                        <Printer className="w-4 h-4 text-slate-600" />
                                        <h3 className="text-sm font-semibold text-slate-700">Print Settings</h3>
                                    </div>
                                    <p className="text-xs text-slate-500 mb-3">
                                        Configure spacing for pre-printed hospital letterhead
                                    </p>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="label">Header Spacing (px)</label>
                                            <input
                                                type="number"
                                                min="0"
                                                max="500"
                                                step="10"
                                                className={`input ${locationErrors.printHeaderSpacing ? 'input-error' : ''}`}
                                                placeholder="100"
                                                {...registerLocation('printHeaderSpacing', { valueAsNumber: true })}
                                            />
                                            {locationErrors.printHeaderSpacing && (
                                                <p className="error-text">{locationErrors.printHeaderSpacing.message}</p>
                                            )}
                                            <p className="text-xs text-slate-500 mt-1">
                                                Space from top (default: 100px)
                                            </p>
                                        </div>
                                        <div>
                                            <label className="label">Footer Spacing (px)</label>
                                            <input
                                                type="number"
                                                min="0"
                                                max="500"
                                                step="10"
                                                className={`input ${locationErrors.printFooterSpacing ? 'input-error' : ''}`}
                                                placeholder="100"
                                                {...registerLocation('printFooterSpacing', { valueAsNumber: true })}
                                            />
                                            {locationErrors.printFooterSpacing && (
                                                <p className="error-text">{locationErrors.printFooterSpacing.message}</p>
                                            )}
                                            <p className="text-xs text-slate-500 mt-1">
                                                Space from bottom (default: 100px)
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="card-footer flex justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={handleCloseLocationModal}
                                    className="btn btn-secondary btn-md"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={createLocationMutation.isPending || updateLocationMutation.isPending}
                                    className="btn btn-primary btn-md"
                                >
                                    {createLocationMutation.isPending || updateLocationMutation.isPending
                                        ? 'Saving...'
                                        : editingLocation
                                            ? 'Update Location'
                                            : 'Add Location'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
